import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface CompletedCallback {
  call_id: string;
  customer_name: string;
  customer_phone: string;
  call_date: string;
  issue_category: string;
  action_required: string;
  callback_status: string;
  callback_agent: string;
  callback_notes: string;
  callback_completed_at: string;
  channel_type: string;
}

const CompletedCallbacks: React.FC = () => {
  const navigate = useNavigate();
  const [callbacks, setCallbacks] = useState<CompletedCallback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedCallback, setSelectedCallback] = useState<CompletedCallback | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const fetchCompletedCallbacks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/callbacks?status=completed&period=30d`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch completed callbacks');
      }

      const data = await response.json();
      setCallbacks(data.callbacks || []);
    } catch (err) {
      console.error('Error fetching completed callbacks:', err);
      setError('Failed to load completed callbacks. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompletedCallbacks();
  }, [fetchCompletedCallbacks]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (callback: CompletedCallback) => {
    setSelectedCallback(callback);
    setDetailsOpen(true);
  };

  const filteredCallbacks = callbacks.filter(callback => {
    const searchLower = searchTerm.toLowerCase();
    return (
      callback.customer_name?.toLowerCase().includes(searchLower) ||
      callback.customer_phone?.toLowerCase().includes(searchLower) ||
      callback.callback_agent?.toLowerCase().includes(searchLower) ||
      callback.issue_category?.toLowerCase().includes(searchLower)
    );
  });

  const paginatedCallbacks = filteredCallbacks.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading && callbacks.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && callbacks.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchCompletedCallbacks}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#080707' }}>
            Completed Callbacks
          </Typography>
          <Typography variant="body2" sx={{ color: '#706E6B' }}>
            Historical record of closed tickets and resolved callbacks
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Tooltip title="Refresh data">
            <IconButton onClick={fetchCompletedCallbacks} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Summary Card */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #66BB6A 0%, #4CAF50 100%)',
            color: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
          }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <CheckCircleIcon sx={{ fontSize: 40, color: 'white' }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
                    {filteredCallbacks.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    Completed Tickets
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by customer, agent, or category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Callbacks Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Call Date</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Closed By</TableCell>
                  <TableCell>Closed Date</TableCell>
                  <TableCell>Channel</TableCell>
                  <TableCell align="center">Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCallbacks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No completed callbacks found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedCallbacks.map((callback) => (
                    <TableRow 
                      key={callback.call_id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/calls/${callback.call_id}`)}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {callback.customer_name || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {callback.customer_phone}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {callback.call_date ? new Date(callback.call_date).toLocaleDateString() : 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {callback.call_date ? new Date(callback.call_date).toLocaleTimeString() : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={callback.issue_category || 'General'} 
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {callback.callback_agent || 'N/A'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {callback.callback_completed_at ? new Date(callback.callback_completed_at).toLocaleDateString() : 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {callback.callback_completed_at ? new Date(callback.callback_completed_at).toLocaleTimeString() : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={callback.channel_type || 'VOICE'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View resolution notes">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetails(callback);
                            }}
                          >
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredCallbacks.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleIcon color="success" />
            <Typography variant="h6">Resolution Details</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedCallback && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Customer</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedCallback.customer_name || 'Unknown'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Issue Category</Typography>
                <Typography variant="body1">
                  {selectedCallback.issue_category || 'General'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Action Required</Typography>
                <Typography variant="body1">
                  {selectedCallback.action_required || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Resolved By</Typography>
                <Typography variant="body1">
                  {selectedCallback.callback_agent || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Completed Date</Typography>
                <Typography variant="body1">
                  {selectedCallback.callback_completed_at ? new Date(selectedCallback.callback_completed_at).toLocaleString() : 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Resolution Notes</Typography>
                <Card variant="outlined" sx={{ mt: 1, p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography 
                    variant="body2" 
                    component="pre"
                    sx={{ 
                      whiteSpace: 'pre-wrap', 
                      fontFamily: 'inherit',
                      margin: 0 
                    }}
                  >
                    {selectedCallback.callback_notes || 'No notes provided'}
                  </Typography>
                </Card>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setDetailsOpen(false)}
          >
            Close
          </Button>
          <Button
            onClick={() => navigate(`/calls/${selectedCallback?.call_id}`)}
            variant="contained"
          >
            View Full Call Details
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompletedCallbacks;

